import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import type { ComponentProps, ForwardedRef, ReactNode } from 'react';
import { forwardRef, useEffect, useId, useRef, useState } from 'react';

import Text from './Text';

const DIGITS_0_TO_9 = Array.from({ length: 10 }).map((_, i) => i);

type Props = {
  value: number;
  variant: ComponentProps<typeof Text>['variant'];
  suffixElement?: ReactNode;
  prefixElement?: ReactNode;
  isActive?: boolean;
} & ComponentProps<'div'>;

const AnimateNumber = (
  {
    value,
    className,
    variant,
    suffixElement,
    prefixElement,
    isActive = true,
    ...props
  }: Props,
  ref: ForwardedRef<HTMLDivElement>
) => {
  const [digitSlotHeight, setDigitSlotHeight] = useState(0);

  const uuid = useId();

  const digitRef = useRef<HTMLDivElement>(null);

  const digitsArrayFromValue = value
    .toString()
    .split('')
    .map((d) => Number(d));

  const [previousDigitsSnapshot, setPreviousDigitsSnapshot] = useState(
    digitsArrayFromValue.map(() => 0)
  );

  useEffect(() => {
    setPreviousDigitsSnapshot(digitsArrayFromValue);
  }, [value]);

  useEffect(() => {
    if (digitRef.current) {
      setDigitSlotHeight(digitRef.current.getBoundingClientRect().height);
    }
  }, []);

  const slotCount = digitsArrayFromValue.length;

  return (
    <div
      {...props}
      ref={ref}
      className={classNames('overflow-hidden w-fit px-px', className)}
      style={{ height: digitSlotHeight }}
    >
      <AnimatePresence>
        <motion.div className="flex flex-row" layout>
          {prefixElement}
          {digitsArrayFromValue.map((currentDigit, slotIndexFromLeft) => {
            const slotIndexFromRight = slotCount - 1 - slotIndexFromLeft;

            const previousDigit =
              previousDigitsSnapshot[
                previousDigitsSnapshot.length - 1 - slotIndexFromLeft
              ] ?? 0;

            return (
              <motion.div
                key={`${uuid}-slot-${slotIndexFromRight}`}
                initial={{
                  y: -(digitSlotHeight * previousDigit),
                }}
                transition={{ duration: 2 }}
                animate={
                  isActive
                    ? { y: -(digitSlotHeight * currentDigit) }
                    : { y: -(digitSlotHeight * previousDigit) }
                }
                className="flex h-fit flex-col items-center"
              >
                {DIGITS_0_TO_9.map((digit, digitIdx) => (
                  <div
                    key={digit}
                    ref={digit === 0 && digitIdx === 0 ? digitRef : undefined}
                  >
                    <Text variant={variant}>{digit}</Text>
                  </div>
                ))}
              </motion.div>
            );
          })}
          {suffixElement}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

AnimateNumber.displayName = 'Block';
export default forwardRef(AnimateNumber);
